import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
// por su ruta: el index de ai-chat trae el AI SDK y esto va en el bundle inicial
import { BpAgentLauncherComponent } from '@bp-core/src/lib/ai-chat/components/launcher/bp-agent-launcher.component';
import { filter, map, startWith } from 'rxjs';
import { AdvisorAiChatLoader } from '../asesor-ia.loader';
import { ADVISOR_AI_CHAT_ROUTE } from '../asesor-ia.models';

/**
 * Nombre del cliente para el botón, completo ("Conversar sobre Inversiones Samira SpA"): cortarlo en la primera palabra
 * confunde con razones sociales. Si no cabe, el botón lo recorta con elipsis y el nombre accesible lo lleva entero.
 */
export function launcherClientName(displayName: string | null | undefined): string | null {
  const name = displayName?.trim().replace(/\s+/g, ' ');
  return name || null;
}

/**
 * Botón flotante del Asesor IA en el layout de bp-advisor (049, T111/T112). Va en el bundle inicial y es liviano: el
 * chat se descarga al pasar el mouse o al abrirlo, y el panel lateral se monta una vez (lo comparte con la página
 * `/chat`, donde el botón no se muestra). En la ficha de un cliente abre el chat con ese cliente ya elegido.
 *
 * Solo con el Asesor IA activo para el asesor (`GetAvailability.mode = full`); sin él no se pinta.
 */
@Component({
  selector: 'asesor-ia-launcher',
  standalone: true,
  imports: [BpAgentLauncherComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <bp-agent-launcher
        [label]="label()"
        closeLabel="Cerrar el Asesor IA"
        previewTitle="El Asesor IA respondió"
        [busy]="busy()"
        [unread]="unread()"
        [preview]="preview()"
        [animateEnter]="!loader.panelMounted()"
        (activate)="toggle()"
        (openPreview)="toggle()"
        (intent)="loader.preload()"
      />
    }
    @if (loader.loadState() === 'error') {
      <p class="asesor-ia-fab-error" role="alert">
        No se pudo cargar el Asesor IA.
        <button type="button" (click)="toggle()">Reintentar</button>
      </p>
    }
  `,
  styleUrl: './asesor-ia-launcher.component.scss',
})
export class AsesorIaLauncherComponent {
  readonly loader = inject(AdvisorAiChatLoader);
  private readonly router = inject(Router);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );
  /** En la página del chat no se muestra (la página ya es el chat). */
  private readonly onChatPage = computed(() => {
    const path = this.url().split(/[?#]/)[0];
    return path === ADVISOR_AI_CHAT_ROUTE || path.startsWith(`${ADVISOR_AI_CHAT_ROUTE}/`);
  });

  private readonly session = computed(() => (this.loader.panelMounted() ? this.loader.loaded?.session ?? null : null));
  readonly panelOpen = computed(() => !!this.session()?.open());
  readonly visible = computed(
    () => this.loader.canUseChat() && this.loader.mode() === 'full' && !this.onChatPage() && !this.panelOpen(),
  );
  /** Respondiendo, o descargando el chat. */
  readonly busy = computed(() => this.loader.loadState() === 'loading' || !!this.session()?.busy());
  readonly unread = computed(() => !!this.session()?.unread());
  readonly preview = computed(() => (this.unread() ? this.session()?.preview() || null : null));
  readonly label = computed(() => {
    const name = launcherClientName(this.loader.pageClient()?.displayName);
    return name ? `Conversar sobre ${name}` : 'Consultar al Asesor IA';
  });

  async toggle(): Promise<void> {
    try {
      await this.loader.togglePanel();
    } catch {
      /* el estado de carga muestra el error y "Reintentar" */
    }
  }
}
